import { explicitMaybe, stringLiteral } from '@execonline-inc/decoders';
import { toResult } from '@execonline-inc/maybe-adapter';
import { toUrlR, URLParser } from '@execonline-inc/url';
import { identity } from '@kofno/piper';
import {
  array,
  boolean,
  dateISO,
  Decoder,
  fail,
  field,
  keyValuePairs as keyValuePairsDecoder,
  number,
  oneOf,
  string,
  succeed,
} from 'jsonous';
import { just, nothing } from 'maybeasy';
import { err, ok } from 'resulty';
import { nullableField, readonlyArray } from '../../../Decoders';
import { findLink } from '../../../LinkyLinky';
import { whenEqualTo, whenGreaterThanOrEqualTo } from '../../../ResultExt';
import { linksDecoder, relDecoder, resourceDecoder } from '../../Decoders';
import { Link, Rel } from '../../Types';
import { multiValueOptions, singleValueOption } from '../Inputs/MultipleSelectionsInput';
import {
  Base64Content,
  BooleanField,
  Content,
  DateField,
  DateScope,
  DeleteFile,
  FieldMode,
  FileField,
  FileValue,
  FormField,
  FormFieldBase,
  FormTemplate,
  FormTemplates,
  ImageContentType,
  isImageContentType,
  isTextContentType,
  KeepFile,
  MultipleSelectionsField,
  NewFile,
  NewFileContent,
  NumericField,
  OptionImage,
  OptionsList,
  OptionsListAndRel,
  OptionsListOrRelOrBoth,
  OptionsRel,
  SelectionOptionResourceWithInnerResource,
  SelectionOptionWithResource,
  SingleSelectionField,
  StringField,
  TextContentType,
  TypeAndFilename,
  UploadedContent,
  UploadLocationRequest,
  UploadLocationResource,
} from '../Types';
import { selectionOptionDecoder } from './SelectionOptionDecoders';

export const fieldModeDecoder: Decoder<FieldMode> = oneOf([
  stringLiteral<FieldMode>('readonly'),
  stringLiteral<FieldMode>('required'),
  stringLiteral<FieldMode>('writable'),
]);

export const formFieldBaseDecoder = <K extends string>(kind: K): Decoder<FormFieldBase<K>> =>
  succeed({})
    .assign('kind', field('kind', stringLiteral(kind)))
    .assign('name', field('name', string))
    .assign('label', nullableField('label', string))
    .assign('errors', field('errors', readonlyArray(string)))
    .assign('mode', field('mode', fieldModeDecoder));

export const textContentTypeDecoder: Decoder<TextContentType> = string.andThen((a) =>
  isTextContentType(a)
    ? succeed(a)
    : fail(`Expected a string content type matching "text/*", but received ${JSON.stringify(a)}`),
);

export const contentDecoder: Decoder<Content> = succeed({})
  .assign('type', field('type', textContentTypeDecoder))
  .assign('multiline', field('multiline', boolean));

export const regExpDecoder: Decoder<RegExp> = string
  .mapError((e) => `Error decoding RegExp: ${e}`)
  .andThen((a) => {
    try {
      return succeed(new RegExp(a));
    } catch {
      return fail(`Expected a valid RegExp string but received ${JSON.stringify(a)}`);
    }
  });

export const floorDecoder = <T>(floor: T, decoder: Decoder<T>): Decoder<T> =>
  decoder.andThen((t) => new Decoder(() => whenGreaterThanOrEqualTo(floor)(t)));

export const singleValueFloorDecoder = <T>(floor: T, decoder: Decoder<T>): Decoder<T> =>
  decoder.andThen((t) => new Decoder(() => whenEqualTo(floor)(t)));

export const stringFieldDecoder: Decoder<StringField> = formFieldBaseDecoder('string')
  .assign('value', field('value', string))
  .assign('content', field('content', contentDecoder))
  .assign('minlength', nullableField('minlength', floorDecoder(0, number)))
  .assign('maxlength', ({ minlength }) =>
    field('maxlength', floorDecoder(minlength.getOrElseValue(0), number)),
  )
  .assign('pattern', nullableField('pattern', regExpDecoder))
  .assign('placeholder', nullableField('placeholder', string));

export const booleanFieldDecoder: Decoder<BooleanField> = formFieldBaseDecoder('boolean').assign(
  'value',
  field('value', boolean),
);

export const dateScopeDecoder: Decoder<DateScope> = oneOf([
  stringLiteral<DateScope>('year'),
  stringLiteral<DateScope>('month'),
  stringLiteral<DateScope>('day'),
  stringLiteral<DateScope>('minute'),
  stringLiteral<DateScope>('second'),
  stringLiteral<DateScope>('millisecond'),
]);

export const dateFieldDecoder: Decoder<DateField> = formFieldBaseDecoder('date')
  .assign('value', field('value', explicitMaybe(dateISO)))
  .assign('min', nullableField('min', dateISO))
  .assign('max', ({ min }) =>
    nullableField('max', min.map((floor) => floorDecoder(floor, dateISO)).getOrElseValue(dateISO)),
  )
  .assign('scope', field('scope', dateScopeDecoder));

export const numericFieldDecoder: Decoder<NumericField> = formFieldBaseDecoder('number')
  .assign('value', field('value', explicitMaybe(number)))
  .assign('min', nullableField('min', number))
  .assign('max', ({ min }) =>
    nullableField('max', min.map((floor) => floorDecoder(floor, number)).getOrElseValue(number)),
  );

export const imageContentTypeDecoder: Decoder<ImageContentType> = string.andThen((a) =>
  isImageContentType(a)
    ? succeed(a)
    : fail(`Expected a string content type matching "image/*", but received ${JSON.stringify(a)}`),
);

export const urlDecoder: Decoder<URLParser> = string
  .mapError((e) => `Error decoding URL: ${e}`)
  .andThen(
    (a) =>
      new Decoder(() =>
        toUrlR(a).mapError(() => `Expected a valid URL but received: ${JSON.stringify(a)}`),
      ),
  );

export const optionImageDecoder: Decoder<OptionImage> = succeed({})
  .assign('type', field('type', imageContentTypeDecoder))
  .assign('data', field('data', string))
  .assign('src', field('src', urlDecoder))
  .assign('alt', field('alt', string));

const valueDecoder: Decoder<string> = oneOf<string | number>([
  string.map<string | number>(identity),
  number.map<string | number>(identity),
]).map(String);

const linkDecoder: Decoder<Link> = new Decoder((u) =>
  linksDecoder
    .decodeAny([u])
    .andThen((a) =>
      a.length === 1
        ? ok(a[0])
        : err(`Expected a valid Link object, but instead got ${JSON.stringify(u)}`),
    ),
);

// selectionOptionDecoder and selectionOptionResourceDecoder are now imported from SelectionOptionDecoders.ts

export const selectionOptionWithResourceDecoder = <T>(
  decoder: Decoder<T>,
): Decoder<SelectionOptionWithResource<T>> =>
  selectionOptionDecoder.assign('resource', field('resource', resourceDecoder(decoder)));

export const selectionOptionResourceWithInnerResourceDecoder = <T>(
  decoder: Decoder<T>,
): Decoder<SelectionOptionResourceWithInnerResource<T>> =>
  resourceDecoder(array(selectionOptionWithResourceDecoder(decoder)));

export const optionsListAndRelDecoder: Decoder<OptionsListAndRel> = succeed({})
  .assign('options', field('options', readonlyArray(selectionOptionDecoder)))
  .assign('optionsRel', field('optionsRel', relDecoder))
  .assign('kind', succeed('options-list-and-rel' as const));

export const optionsListDecoder: Decoder<OptionsList> = succeed({})
  .assign('options', field('options', readonlyArray(selectionOptionDecoder)))
  .assign('kind', succeed('options-list' as const));

export const optionsRelDecoder: Decoder<OptionsRel> = succeed({})
  .assign('optionsRel', field('optionsRel', relDecoder))
  .assign('kind', succeed('options-rel' as const));

export const optionsListOrRelOrBothDecoder: Decoder<OptionsListOrRelOrBoth> = oneOf([
  optionsListAndRelDecoder.map<OptionsListOrRelOrBoth>(identity),
  optionsListDecoder.map<OptionsListOrRelOrBoth>(identity),
  optionsRelDecoder.map<OptionsListOrRelOrBoth>(identity),
]);

export const singleSelectionFieldPartDecoder = (
  links: ReadonlyArray<Link>,
): Decoder<SingleSelectionField> =>
  formFieldBaseDecoder('multiple-selection')
    .assign('value', field('value', readonlyArray(valueDecoder)))
    .assign('minSelections', field('minSelections', singleValueFloorDecoder(1, number)))
    .assign('maxSelections', field('maxSelections', singleValueFloorDecoder(1, number)))
    .assign('options', optionsListOrRelOrBothDecoder)
    .assign('links', succeed(links))
    .map((field) => {
      const { value, options, ...rest } = field;

      return {
        ...rest,
        value: singleValueOption(just(value[0]), options),
        kind: 'single-selection',
        options: options,
      };
    });

export const multipleSelectionsFieldDecoder = (
  links: ReadonlyArray<Link>,
): Decoder<MultipleSelectionsField> =>
  formFieldBaseDecoder('multiple-selection')
    .assign('minSelections', field('minSelections', floorDecoder(0, number)))
    .assign('maxSelections', ({ minSelections }) =>
      field('maxSelections', floorDecoder(minSelections, number)),
    )
    .assign('value', field('value', readonlyArray(valueDecoder)))
    .assign('options', optionsListOrRelOrBothDecoder)
    .assign('links', succeed(links))
    .map((field) => {
      const { value, options, ...rest } = field;

      return {
        ...rest,
        value: multiValueOptions(value, options),
        options: options,
      };
    });

export const uploadLocationRequestDecoder: Decoder<UploadLocationRequest> = succeed({})
  .assign('filename', field('filename', string))
  .assign('fieldName', field('fieldName', string));

export const uploadLocationResourceDecoder: Decoder<UploadLocationResource> = resourceDecoder(
  uploadLocationRequestDecoder,
);

export const typeAndFilenameDecoder: Decoder<TypeAndFilename> = succeed({})
  .assign('type', field('type', string))
  .assign('filename', field('filename', string));

export const base64ContentDecoder: Decoder<Base64Content> = succeed({})
  .assign('kind', field('kind', stringLiteral('base64-content')))
  .assign('data', field('data', string));

export const uploadedContentDecoder: Decoder<UploadedContent> = succeed({})
  .assign('kind', field('kind', stringLiteral('uploaded-content')))
  .assign('uploadLocation', field('uploadLocation', linkDecoder));

export const newFileContentDecoder: Decoder<NewFileContent> = oneOf([
  base64ContentDecoder.map<NewFileContent>(identity),
  uploadedContentDecoder.map<NewFileContent>(identity),
]);

export const newFileDecoder: Decoder<NewFile> = typeAndFilenameDecoder
  .assign('kind', field('kind', stringLiteral('new-file')))
  .assign('dataUrl', nullableField('dataUrl', string))
  .assign('content', nullableField('content', newFileContentDecoder))
  .assign('file', succeed(nothing<File>()));

export const keepFileDecoder: Decoder<KeepFile> = typeAndFilenameDecoder
  .assign('kind', field('kind', stringLiteral('keep-file')))
  .assign('src', field('src', urlDecoder))
  .assign('data', nullableField('data', string));

export const deleteFileDecoder: Decoder<DeleteFile> = typeAndFilenameDecoder
  .assign('kind', field('kind', stringLiteral('delete-file')))
  .assign('src', field('src', urlDecoder))
  .assign('data', nullableField('data', string));

export const fileValueDecoder: Decoder<FileValue> = oneOf([
  keepFileDecoder.map<FileValue>(identity),
  newFileDecoder.map<FileValue>(identity),
  deleteFileDecoder.map<FileValue>(identity),
]);

export const findLinkDecoder =
  (links: ReadonlyArray<Link>) =>
  (rel: Rel): Decoder<Link> =>
    new Decoder(() =>
      toResult<string, Link>(
        `Expected to find link with rel ${rel}, but did not.`,
        findLink(rel, links),
      ),
    );

const createFileUploadLocationLinkDecoder = (links: ReadonlyArray<Link>) =>
  field('createFileUploadLocationRel', relDecoder).andThen(findLinkDecoder(links));

export const fileFieldDecoder = (links: ReadonlyArray<Link>): Decoder<FileField> =>
  formFieldBaseDecoder('file')
    .assign('value', field('value', readonlyArray(fileValueDecoder)))
    .assign('accept', field('accept', readonlyArray(string)))
    .assign('multiple', field('multiple', boolean))
    .assign('maxSize', field('maxSize', number))
    .assign('createFileUploadLocationLink', createFileUploadLocationLinkDecoder(links));

export const formFieldDecoder = (links: ReadonlyArray<Link>): Decoder<FormField> =>
  oneOf([
    stringFieldDecoder.map<FormField>(identity),
    booleanFieldDecoder.map<FormField>(identity),
    dateFieldDecoder.map<FormField>(identity),
    numericFieldDecoder.map<FormField>(identity),
    singleSelectionFieldPartDecoder(links).map<FormField>(identity),
    multipleSelectionsFieldDecoder(links).map<FormField>(identity),
    fileFieldDecoder(links).map<FormField>(identity),
  ]);

export const formTemplateDecoder = (links: ReadonlyArray<Link>): Decoder<FormTemplate> =>
  succeed({})
    .assign('target', field('target', relDecoder))
    .assign('fields', field('fields', readonlyArray(formFieldDecoder(links))))
    .assign('errors', field('errors', readonlyArray(string)));

export const formTemplatesDecoder = (links: ReadonlyArray<Link>): Decoder<FormTemplates> =>
  keyValuePairsDecoder(formTemplateDecoder(links)).andThen(
    (keyValuePairs) =>
      new Decoder(() =>
        readonlyArray(relDecoder)
          .decodeAny(keyValuePairs.map(([key, _template]) => key))
          .map<FormTemplates>((keys) =>
            keys.reduce((templates, key, i) => ({ ...templates, [key]: keyValuePairs[i][1] }), {}),
          ),
      ),
  );
